
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  VStack,
  Text,
  HStack,
  Icon,
  useToast,
  Select,
  Button,
  Textarea,
  IconButton,
} from "@chakra-ui/react";
import { FaClock } from "react-icons/fa";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import SlideToComplete from "./SlideToComplete";
import {
  PunchIn,
  PunchOut,
  StartBreak,
  EndBreak,
  getAttendanceStatus,
} from "../../../../API/attendance";

dayjs.extend(duration);

const AttendanceSystem = ({ billerId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [attendanceStatus, setAttendanceStatus] = useState(null);
  const [punchInTime, setPunchInTime] = useState(null);
  const [punchOutTime, setPunchOutTime] = useState(null);
  const [isPunchedIn, setIsPunchedIn] = useState(false);
  const [currentBreak, setCurrentBreak] = useState(null);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakType, setBreakType] = useState("Tea Break");
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [punchOutDescription, setPunchOutDescription] = useState("");
  const [isPunchOutModalOpen, setIsPunchOutModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(dayjs()), 1000);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        setCurrentTime(dayjs()); // Refresh time when tab becomes active
      }
    });
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", () => {});
    };
  }, []);

  const fetchAttendanceStatus = useCallback(async () => {
    try {
      const response = await getAttendanceStatus(billerId);
      if (!response?.attendance) {
        // If no attendance data, ensure default state
        setPunchInTime(null);
        setPunchOutTime(null);
        setIsPunchedIn(false);
        setCurrentBreak(null);
        setBreakStartTime(null);
        setAttendanceStatus(null);
        return;
      }

      setPunchInTime(
        response.attendance.punchInTime
          ? dayjs(response.attendance.punchInTime)
          : null
      );
      setPunchOutTime(
        response.attendance.punchOutTime
          ? dayjs(response.attendance.punchOutTime)
          : null
      );
      setIsPunchedIn(
        !!response.attendance.punchInTime && !response.attendance.punchOutTime
      );
      setCurrentBreak(response.attendance.breakType || null);
      setBreakStartTime(
        response.attendance.breakStartTime
          ? dayjs(response.attendance.breakStartTime)
          : null
      );
      setAttendanceStatus(response.attendance);
    } catch (error) {
      toast({
        title: "Error fetching attendance status!",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
      });
    }
  }, [billerId, toast]);

  useEffect(() => {
    fetchAttendanceStatus();
  }, [fetchAttendanceStatus]);

  const calculateDuration = (start, end = dayjs()) => {
    if (!start) return "00:00:00";
    const diff = end.diff(start);
    const duration = dayjs.duration(diff);
    return `${String(duration.hours()).padStart(2, "0")}:${String(
      duration.minutes()
    ).padStart(2, "0")}:${String(duration.seconds()).padStart(2, "0")}`;
  };

  const workDuration = isPunchedIn
    ? calculateDuration(punchInTime, punchOutTime || currentTime)
    : "00:00:00";

  const breakDuration = currentBreak
    ? calculateDuration(breakStartTime, currentTime)
    : "00:00:00";

  const handlePunchIn = async () => {
    try {
      await PunchIn(billerId);
      setPunchInTime(dayjs());
      setIsPunchedIn(true);
      setIsOpen(false);
      toast({ title: "Punched In!", status: "success" });
    } catch (error) {
      toast({
        title: "Punch In Failed!",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
      });
    }
  };

  const handlePunchOut = async () => {
    setIsPunchOutModalOpen(true); // Show the modal for entering the description
    setIsOpen(false);
  };

  const submitPunchOut = async () => {
    setIsLoading(true);
    if (!punchOutDescription.trim()) {
      toast({
        title: "Work Summary Required!",
        description: "Please enter what you worked on today.",
        status: "warning",
      });
      return;
    }

    try {
      await PunchOut(billerId, punchOutDescription);
      setPunchOutTime(dayjs());
      setIsPunchedIn(false);
      setIsPunchOutModalOpen(false);
      setPunchOutDescription(""); // Reset input
      toast({ title: "Punched Out!", status: "success" });
    } catch (error) {
      toast({
        title: "Punch Out Failed!",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
      });
    }
    finally {
      setIsLoading(false);
      }
  };

  const handleStartBreak = async () => {
    try {
      await StartBreak(billerId, breakType);
      setCurrentBreak(breakType);
      setBreakStartTime(dayjs());
      toast({ title: `${breakType} Started!`, status: "success" });
    } catch (error) {
      toast({
        title: "Failed to Start Break!",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
      });
    }
  };

  const handleEndBreak = async () => {
    try {
      await EndBreak(billerId);
      setCurrentBreak(null);
      setBreakStartTime(null);
      toast({ title: "Break Ended!", status: "success" });
    } catch (error) {
      toast({
        title: "Failed to End Break!",
        description: error.response?.data?.message || "Something went wrong.",
        status: "error",
      });
    }
  };

  return (
    <>
      <Box position="relative" display="inline-block">
        <IconButton
          icon={<FaClock />}
          size="sm"
          colorScheme="blue"
          ml={2}
          onClick={() => setIsOpen(true)}
          // className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 animate-pulse"
        />
      </Box>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        isCentered
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">
            <HStack justifyContent="center" spacing={2}>
              <Icon as={FaClock} color="teal.500" />
              <Text>Attendance System</Text>
            </HStack>
          </ModalHeader>
          <ModalBody>
            <VStack spacing={6}>
              <Text fontSize="lg" fontWeight="bold" color="teal.600">
                Work Duration: {workDuration}
              </Text>

              {currentBreak && (
                <Text fontSize="lg" fontWeight="bold" color="orange.600">
                  {currentBreak} Active: {breakDuration}
                </Text>
              )}

              {!isPunchedIn ? (
                <SlideToComplete
                  text="Slide to Punch In"
                  onComplete={handlePunchIn}
                />
              ) : punchOutTime ? (
                <Text fontSize="lg" color="green.500">
                  Punched Out at {punchOutTime.format("hh:mm A")}
                </Text>
              ) : (
                <>
                  {!currentBreak ? (
                    <>
                      <Select
                        placeholder="Select Break Type"
                        onChange={(e) => setBreakType(e.target.value)}
                      >
                        <option value="Tea Break">Tea Break</option>
                        <option value="Lunch Break">Lunch Break</option>
                        <option value="Permission Break">
                          Permission Break
                        </option>
                      </Select>
                      <SlideToComplete
                        text="Slide to Start Break"
                        onComplete={handleStartBreak}
                      />
                    </>
                  ) : (
                    <SlideToComplete
                      text="Slide to End Break"
                      onComplete={handleEndBreak}
                    />
                  )}
                  <SlideToComplete
                    text="Slide to Punch Out"
                    onComplete={handlePunchOut}
                  />
                </>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* Punch-Out Modal */}
      <Modal
        isOpen={isPunchOutModalOpen}
        onClose={() => setIsPunchOutModalOpen(false)}
        isCentered
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Punch Out Summary</ModalHeader>
          <ModalBody>
            <Textarea
              placeholder="Briefly describe today's work..."
              value={punchOutDescription}
              onChange={(e) => setPunchOutDescription(e.target.value)}
              borderColor={!punchOutDescription.trim() ? "red.500" : "gray.200"}
            />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="teal" isLoading={isLoading} loadingText="Submitting..." onClick={submitPunchOut}>
              Submit & Punch Out
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AttendanceSystem;